import {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  gameCategories as fetchCategories,
  GameCategory,
  Game,
  games as fetchGames,
  providers as fetchProviders,
  Provider,
  favoriteGames as requestFavoriteGame,
  setFavoriteGame as requestSetFavoriteGame,
  deleteFavoriteGame as requestDeleteFavoriteGame,
} from '../../api/games';
import { useApi } from '../../hooks/api';
import { AuthContext } from '../auth';
import { isMobileDevice, sortAscending } from '../../helpers/common';
import { GameType } from '../../helpers/enums';
import { latestWinners, Winner } from '../../api/winners';
import { getGamePath } from '../../helpers/game';
import { addGameToRecentViewedStorage, getRecentGames } from './helpers';

export interface GamesContextData {
  categories: GameCategory[];
  categoriesByRegEx: (regEx: RegExp) => GameCategory[];
  pendingCategories: boolean;
  games: Game[];
  pendingGames: boolean;
  providers: Provider[];
  pendingProviders: boolean;
  favoriteGames: number[];
  recentGames: Game[];
  winners: Winner[];
  pendingWinners: boolean;
}

export interface GamesContextAction {
  addFavoriteGames: (currencyCode: Game['real']) => Promise<void>;
  removeFavoriteGames: (currencyCode: Game['real']) => Promise<void>;
  addGameToRecentList: (game: Game) => void;
}

export const GamesContext = createContext<
  GamesContextData & GamesContextAction
>(null as any);

export const GamesContextProvider: FC = ({ children }) => {
  const {
    i18n: { language },
  } = useTranslation();

  const { isAuthenticated, user } = useContext(AuthContext);

  const [favoriteGames, setFavoriteGames] = useState<number[]>([]);
  const [recentGames, setRecentGames] = useState<Game[]>([]);

  const [
    requestGameCategories,
    { data: dataCategories, pending: pendingCategories },
  ] = useApi(fetchCategories);

  const [requestGames, { data: dataGames, pending: pendingGames }] = useApi(
    fetchGames
  );

  const [
    requestProviders,
    { data: dataProviders, pending: pendingProviders },
  ] = useApi(fetchProviders);

  const [
    requestWinners,
    { data: dataWinners, pending: pendingWinners },
  ] = useApi(latestWinners);

  useEffect(() => {
    if (language != null) {
      requestGameCategories(language);
    }
  }, [requestGameCategories, language]);

  useEffect(() => {
    requestProviders();
    requestWinners();
  }, [requestProviders, requestWinners]);

  useEffect(() => {
    const type = isMobileDevice ? GameType.MOBILE : GameType.DESKTOP;
    requestGames(type);
  }, [requestGames]);

  const fetchFavoriteGame = useCallback(async () => {
    try {
      const { data: fg } = await requestFavoriteGame();
      setFavoriteGames(fg);
    } catch {}
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    fetchFavoriteGame();
  }, [fetchFavoriteGame, isAuthenticated]);

  const categories = useMemo(() => {
    if (dataCategories == null) return [];

    return dataCategories.filter(
      ({ id }) => /^_top_/.test(id) || (!/^_/.test(id) && /^[a-z]/.test(id))
    );
  }, [dataCategories]);

  const categoriesByRegEx = useCallback(
    (regEx: RegExp) => {
      if (dataCategories == null) return [];

      return dataCategories.filter(({ id }) => regEx.test(id));
    },
    [dataCategories]
  );

  const games = useMemo(() => {
    if (dataGames == null) return [];

    return Object.keys(dataGames).map((id) => {
      const game = dataGames[id];
      const path = getGamePath(game.title);

      return {
        id,
        listCollections: Object.keys(game.collections),
        path,
        fullPath: `${game.provider}/${path}`,
        seoPath: `${path}-${game.provider}`,
        ...game,
      };
    });
  }, [dataGames]);

  const providers = useMemo(() => {
    if (games == null || dataProviders == null) return [];

    const availableProviderIDs = games
      .map(({ provider }) => provider)
      .filter((pr, i, prs) => prs.indexOf(pr) === i);

    const availableProviders = dataProviders
      .filter(({ id }) => availableProviderIDs.includes(id))
      .map((provider) => ({
        ...provider,
        numberOfGames: games.filter((game) => game.provider === provider.id)
          .length,
      }));

    return sortAscending(availableProviders, 'title');
  }, [games, dataProviders]);

  const winners = useMemo(() => {
    if (dataWinners == null) return [];

    return dataWinners.map((winner) => ({
      ...winner,
      path: getGamePath(winner.gameTitle),
    }));
  }, [dataWinners]);

  useEffect(() => {
    setRecentGames(getRecentGames(games));
  }, [games]);

  const addFavoriteGames = useCallback(
    async (currencies: Game['real']) => {
      if (user?.currency == null || currencies == null) return;

      const currencyCode = currencies[user.currency].id;

      try {
        await requestSetFavoriteGame(currencyCode);
        setFavoriteGames((s) => [...s, currencyCode]);
      } catch {}
    },
    [user]
  );

  const removeFavoriteGames = useCallback(
    async (currencies: Game['real']) => {
      if (currencies == null) return;

      const favoriteGame = Object.values(currencies)
        .map(({ id }) => id)
        .find((c) => favoriteGames.includes(c));

      if (favoriteGame != null) {
        try {
          await requestDeleteFavoriteGame(favoriteGame);
          setFavoriteGames((s) => s.filter((g) => g !== favoriteGame));
        } catch {}
      }
    },
    [favoriteGames]
  );

  const addGameToRecentList = useCallback(
    (game: Game) => {
      addGameToRecentViewedStorage(game);
      setRecentGames(getRecentGames(games));
    },
    [games]
  );

  return (
    <GamesContext.Provider
      value={{
        categories,
        categoriesByRegEx,
        pendingCategories,
        games,
        pendingGames,
        providers,
        pendingProviders,
        favoriteGames,
        addFavoriteGames,
        removeFavoriteGames,
        recentGames,
        addGameToRecentList,
        winners,
        pendingWinners,
      }}
    >
      {children}
    </GamesContext.Provider>
  );
};
